/**
 * Capture workflow reducer (ported from Noteble — ADR-022).
 *
 * Pure function: (state, event) => state. No side effects, no async, no I/O.
 * Every transition is explicit — an unhandled event returns state unchanged,
 * so the reducer is safe to call with any event at any time.
 */

import type { WorkflowContext, WorkflowEvent } from './types';
import { initialContext } from './transitions';

export function workflowReducer(
  state: WorkflowContext,
  event: WorkflowEvent,
): WorkflowContext {
  switch (event.type) {
    // ── Capture mutations (step-agnostic — allowed from any step for restore) ──

    case 'SET_INPUT_METHOD':
      return { ...state, inputMethod: event.method, error: null };

    case 'SET_NOTE':
      return { ...state, note: event.text };

    case 'CAPTURE_STREAMING':
      return {
        ...state,
        capturedStreams: [
          ...state.capturedStreams,
          { id: event.id, duration: event.duration, transcript: event.transcript },
        ],
      };

    case 'DISCARD_STREAMING_TRANSCRIPT':
      return {
        ...state,
        capturedStreams: state.capturedStreams.filter((s) => s.id !== event.id),
      };

    // ── Navigation ──

    case 'GOTO_STEP':
      return { ...state, step: event.step, error: null };

    // ── Template selection ──

    case 'TOGGLE_TEMPLATE': {
      const exists = state.selectedTemplates.some((t) => t.id === event.template.id);
      return {
        ...state,
        selectedTemplates: exists
          ? state.selectedTemplates.filter((t) => t.id !== event.template.id)
          : [...state.selectedTemplates, event.template],
      };
    }

    case 'SET_TEMPLATES':
      return { ...state, selectedTemplates: event.templates };

    // ── Submission ──

    case 'SUBMIT_START':
      return { ...state, isSubmitting: true, error: null };

    case 'SUBMIT_SUCCESS':
      // Terminal: reset everything, ready for the next document.
      return { ...initialContext };

    case 'SUBMIT_FAILURE':
      return { ...state, isSubmitting: false, error: event.error ?? null };

    // ── Lifecycle ──

    case 'START_OVER':
      return { ...initialContext };

    case 'SET_ERROR':
      return { ...state, error: event.error };

    case 'SET_ACTIVE_DRAFT_ID':
      return { ...state, activeDraftId: event.id };

    // ── Draft restore ──

    case 'RESTORE_DRAFT_START':
      return { ...state, isRestoring: true };

    case 'RESTORE_DRAFT_COMPLETE':
      return {
        ...state,
        ...event.payload,
        isRestoring: false,
        error: null,
      };

    // ── Workflow persistence restore (from localStorage on mount) ──

    case 'RESTORE_WORKFLOW':
      return {
        ...state,
        ...event.payload,
        error: null,
      };

    default:
      return state;
  }
}
